import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import PromoPopup from "@/components/PromoPopup";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SESSION_KEY = "silky_promo_dismissed";

const FALLBACK_CONFIG = {
  active: true,
  delay_seconds: 3,
  headline: "Save 25% On Your First Cleaning",
  subhead:
    "Use code CANS25 for 25% off your first cleaning.",
  code: "CANS25",
  cta_text: "Sign Up Now",
  cta_link: "/signup?plan=monthly&promo=CANS25",
  close_text: "No thanks, I'll pay full price",
  disclaimer: "New customers only. One use per customer.",
  logo_src: "/logo-wordmark.png",
  show_on_routes: ["/"],
  hide_when_authenticated: true,
};

function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const handler = (e) => setReduced(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, []);
  return reduced;
}

/**
 * Triggers the PromoPopup ~3s after first visit to /. Respects
 * sessionStorage dismissal flag, hides when authenticated, hides on
 * /signup /signin /portal so we don't re-prompt during checkout.
 *
 * TODO Phase 4: Promo Popup Manager admin UI will edit /api/admin/site-config
 * → /api/promo-popup, no code changes required to swap copy/code/link.
 */
export default function PromoPopupManager() {
  const { pathname } = useLocation();
  const { isAuthed } = useAuth();
  const reduced = useReducedMotion();

  const [config, setConfig] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/promo-popup")
      .then(({ data }) => {
        if (!cancelled) setConfig(data?.config || FALLBACK_CONFIG);
      })
      .catch(() => {
        if (!cancelled) setConfig(FALLBACK_CONFIG);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!config || !config.active) return;
    if (config.hide_when_authenticated && isAuthed) return;

    const allowed = (config.show_on_routes || ["/"]).includes(pathname);
    if (!allowed) {
      // If user navigates away while popup is showing, close it.
      if (open) setOpen(false);
      return;
    }

    if (typeof window !== "undefined") {
      try {
        if (window.sessionStorage.getItem(SESSION_KEY) === "1") return;
      } catch {
        /* ignore storage failures */
      }
    }

    const delay = Math.max(0, (config.delay_seconds ?? 3) * 1000);
    const t = setTimeout(() => setOpen(true), delay);
    return () => clearTimeout(t);
  }, [config, pathname, isAuthed, open]);

  const handleClose = () => {
    setOpen(false);
    try {
      window.sessionStorage.setItem(SESSION_KEY, "1");
    } catch {
      /* ignore */
    }
  };

  if (!config) return null;
  return (
    <PromoPopup
      open={open}
      onClose={handleClose}
      config={config}
      reduced={reduced}
    />
  );
}
